import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription, switchMap, tap } from 'rxjs';
import {
  AuthService,
  HelpersService,
  HttpBaseService,
  ToastService,
} from '@evento-ng/libs';
import {
  changePasswordApi,
  userApi,
} from 'src/app/shared/constants/api.constants';
import {
  ACCESS_TOKEN,
  REFRESH_TOKEN,
} from 'src/app/shared/constants/general.constants';
import { Toast } from 'src/app/shared/enums/toast';
import { IUser } from 'src/app/shared/models/IUser.model';
import { TranslateService } from '@ngx-translate/core';
@Component({
  selector: 'app-personal-data',
  templateUrl: './personal-data.component.html',
})
export class PersonalDataComponent implements OnInit, OnDestroy {
  PersonalInfoForm: UntypedFormGroup;
  editPasswordForm: UntypedFormGroup;
  showPersonalInfo: boolean;
  showEditPassword: boolean;
  user: IUser;
  subs: Subscription[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private dataService: HttpBaseService,
    private spinner: NgxSpinnerService,
    private helpers: HelpersService,
    private toast: ToastService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private titleService: Title,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.user = this.helpers.getUserFromToken();
    this.titleService.setTitle(
      this.user?.firstName + ' ' + this.user?.lastName
    );
    this.initPersonalInfoForm();
    this.initEditPasswordForm();
  }

  save(): void {
    this.spinner.show();
    const mobile = this.PersonalInfoForm.value.mobile.nationalNumber;
    const mobileRegionCode = this.PersonalInfoForm.value.mobile.countryCode;

    const updatedData = {
      ...this.PersonalInfoForm.value,
      mobile,
      mobileRegionCode,
    };

    this.dataService.patch(`${userApi}`, updatedData).subscribe((user: any) => {
      this.helpers.setItemToLocalStorage(ACCESS_TOKEN, user.access_token);
      this.helpers.setItemToLocalStorage(REFRESH_TOKEN, user.refresh_token);
      this.authService.fetchUser();
      this.user = this.helpers.getUserFromToken();
      this.spinner.hide();
      this.toast.show(
        'Success',
        this.translate.instant('Name has been changed'),
        {
          classname: Toast.success,
        }
      );
    });
  }

  private initPersonalInfoForm(): void {
    let firstName;
    let lastName;
    let email;
    let mobile;

    if (this.user) {
      firstName = this.user.firstName;
      lastName = this.user.lastName;
      email = this.user.email;
      mobile = this.user.mobile;
    }

    this.PersonalInfoForm = this.formBuilder.group({
      firstName: [firstName, Validators.required],
      lastName: [lastName, Validators.required],
      email: [email],
      mobile: [mobile],
    });
  }

  changePassword(): void {
    this.spinner.show();

    this.recaptchaV3Service
      .execute('changePasswordApi')
      .pipe(
        switchMap((token) => {
          return this.dataService.patch(`${changePasswordApi}`, {
            ...this.editPasswordForm.value,
            recaptcha: token,
          });
        }),
        tap((_) => {
          this.spinner.hide();
          this.editPasswordForm.reset();
          this.toast.show(
            'Success',
            this.translate.instant('Password has been changed'),
            {
              classname: Toast.success,
            }
          );
        })
      )
      .subscribe();
  }
  private initEditPasswordForm(): void {
    this.editPasswordForm = this.formBuilder.group(
      {
        currentPassword: ['', [Validators.required, Validators.minLength(6)]  ],
        password: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required, Validators.minLength(6)]],
      },
      {
        validators: [this.MustMatch('password', 'confirmPassword')],
      }
    );
  }
  MustMatch(controlName: string, matchingControlName: string): any {
    return (formGroup: UntypedFormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        // return if another validator has already found an error on the matchingControl
        return;
      }

      // set error on matchingControl if validation fails
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
